import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StructureIssue } from '@peruselab/api-client/structureChecks';
import type {
  ChemicalValidationIssue,
  MolecularProperties
} from '@peruselab/structure-editor/chemistry/chemicalValidation';
import { FormulaText } from '../FormulaText';
import { Icon } from '../Icon';
import { structureFixLabel, structureIssueMessage, type StructureChecksActions } from './StructureChecksSection';

/** One problem the panel lists: the server's check (with its fixes) or, without it, the browser's. */
export interface DrawingProblem {
  /** Rule and atoms: the same problem keeps its key across edits that don't touch it. */
  readonly key: string;
  readonly severity: 'ERROR' | 'WARNING';
  readonly issue?: StructureIssue;
  readonly browserIssue?: ChemicalValidationIssue;
}

/**
 * The open problems on the paper, or null while they aren't known yet. ADR 0020 D1: the server's
 * check is the authority once it has answered; until then, signed out or when it failed, the
 * browser's own check stands. A warning confirmed intended is no longer a problem (D5).
 */
export function drawingProblems(
  serverIssues: readonly StructureIssue[] | null,
  serverPending: boolean,
  browserIssues: readonly ChemicalValidationIssue[] | null
): readonly DrawingProblem[] | null {
  if (serverIssues) {
    return serverIssues
      .filter((issue) => issue.severity !== 'INFO' && !issue.confirmation)
      .map((issue) => ({
        key: `${issue.rule}:${issue.atomIds.join(',')}:${issue.bondIds.join(',')}`,
        severity: issue.severity === 'ERROR' ? 'ERROR' : 'WARNING',
        issue
      }));
  }
  if (serverPending || !browserIssues) return null;
  return browserIssues.map((issue) => ({
    key: `${issue.code}:${issue.targetId ?? ''}`,
    severity: 'ERROR',
    browserIssue: issue
  }));
}

interface DrawingInfoPanelProps {
  readonly metrics: { readonly nodes: number; readonly edges: number; readonly atoms: number; readonly bonds: number };
  readonly hasGraphNodes: boolean;
  /** Null while the check hasn't answered for the drawing as it is. */
  readonly problems: readonly DrawingProblem[] | null;
  /** The browser's properties of the drawing as it is (formula, weight, SMILES, pieces). */
  readonly properties: MolecularProperties | null;
  /** The check couldn't be run at all; Retry asks again. */
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly onShowChecks: () => void;
  readonly actions: Omit<StructureChecksActions, 'onSelect'>;
  readonly autoOpen: boolean;
  readonly onAutoOpenChange: (next: boolean) => void;
  readonly onClose: () => void;
}

const WEIGHT_DIGITS = 3;

/**
 * #1110 (owner review, 2026-10-29; docs/drawing-info-panel.md): the panel behind the top bar's
 * stats icon. The check's verdict first (P1), then what is on the paper piece by piece with an ion
 * pair as one entry (P2, P3), and a Total row (P4). With problems, their one-click fixes -- the
 * Checks list's own (P5) -- and, while there's an error, no formula, weight or SMILES.
 */
export function DrawingInfoPanel({
  metrics,
  hasGraphNodes,
  problems,
  properties,
  failed,
  onRetry,
  onShowChecks,
  actions,
  autoOpen,
  onAutoOpenChange,
  onClose
}: DrawingInfoPanelProps) {
  const { t } = useTranslation();
  const hasError = problems?.some((problem) => problem.severity === 'ERROR') ?? false;
  const pieces = !hasError && properties ? properties.pieces : null;
  const pieceCount = pieces?.reduce((sum, piece) => sum + piece.count, 0) ?? null;

  return (
    <div className="drawing-info">
      <div className="drawing-info-status" data-testid="drawing-info-status">
        <Status hasGraphNodes={hasGraphNodes} problems={problems} failed={failed} onRetry={onRetry} />
        {problems && problems.length > 0 ? (
          <button type="button" className="drawing-info-link" onClick={onShowChecks}>
            {t('drawingInfo.allInChecks')}
          </button>
        ) : null}
        <button
          type="button"
          className="icon-button drawing-info-close"
          aria-label={t('drawingInfo.close')}
          title={t('drawingInfo.close')}
          onClick={onClose}
        >
          <Icon name="close" />
        </button>
      </div>

      {problems && problems.length > 0 ? (
        <ul className="drawing-info-problems" aria-label={t('drawingInfo.problems', { count: problems.length })}>
          {problems.map((problem) => (
            <ProblemEntry key={problem.key} problem={problem} actions={actions} />
          ))}
        </ul>
      ) : null}
      {hasError ? <p className="drawing-info-note">{t('drawingInfo.backOnceFixed')}</p> : null}

      {pieces && pieces.length > 0 ? (
        <ul className="drawing-info-pieces" aria-label={t('drawingInfo.pieces')} data-testid="drawing-info-pieces">
          {pieces.map((piece, index) => (
            <li key={piece.smiles ?? `${piece.formula}-${index}`} className="drawing-info-piece">
              <div className="drawing-info-piece-head">
                <span className="drawing-info-formula" data-testid="drawing-info-formula">
                  <FormulaText formula={piece.formula} />
                </span>
                {piece.ionPair ? <span className="drawing-info-chip">{t('drawingInfo.ionPair')}</span> : null}
                <span className="drawing-info-weight">
                  {t(piece.count > 1 ? 'drawingInfo.weightEach' : 'drawingInfo.weight', {
                    weight: piece.molecularWeight.toFixed(WEIGHT_DIGITS)
                  })}
                </span>
              </div>
              <SmilesLine smiles={piece.smiles} formula={piece.formula} hapto={piece.hapto === true} />
            </li>
          ))}
        </ul>
      ) : null}

      {problems && problems.length > 0 ? (
        <label className="drawing-info-auto-open">
          <input type="checkbox" checked={autoOpen} onChange={(event) => onAutoOpenChange(event.target.checked)} />
          {t('drawingInfo.autoOpen')}
        </label>
      ) : null}

      {/* The graph's own counts (nodes and edges, which include reaction arrows and Markush parts)
          mean nothing to a chemist (owner, #1110); they stay readable for tests only. */}
      <section
        className="drawing-info-total"
        aria-label={t('metricsPanel.ariaLabel')}
        data-nodes={metrics.nodes}
        data-edges={metrics.edges}
      >
        <div className="drawing-info-total-row" data-testid="drawing-info-total">
          {pieceCount !== null && pieceCount > 1 ? (
            <span className="drawing-info-total-label">{t('drawingInfo.total')}</span>
          ) : null}
          <span className="drawing-info-counts">
            {[
              ...(pieceCount !== null ? [t('drawingInfo.piece', { count: pieceCount })] : []),
              t('drawingInfo.atom', { count: metrics.atoms }),
              t('drawingInfo.bond', { count: metrics.bonds })
            ].join(' · ')}
          </span>
          {pieces && pieces.length > 1 && properties ? (
            <span className="drawing-info-total-weight">
              {t('drawingInfo.weight', { weight: properties.molecularWeight.toFixed(WEIGHT_DIGITS) })}
            </span>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function Status({
  hasGraphNodes,
  problems,
  failed,
  onRetry
}: {
  readonly hasGraphNodes: boolean;
  readonly problems: readonly DrawingProblem[] | null;
  readonly failed: boolean;
  readonly onRetry: () => void;
}) {
  const { t } = useTranslation();
  if (!hasGraphNodes) return <span className="drawing-info-verdict">{t('drawingInfo.empty')}</span>;
  if (failed) {
    return (
      <>
        <span className="drawing-info-verdict drawing-info-verdict--failed">
          <Icon name="error" />
          {t('drawingInfo.failed')}
        </span>
        <button type="button" className="drawing-info-link" onClick={onRetry}>
          <Icon name="refresh" />
          {t('drawingInfo.retry')}
        </button>
      </>
    );
  }
  if (problems === null) return <span className="drawing-info-verdict">{t('drawingInfo.checking')}</span>;
  if (problems.length > 0) {
    return (
      <span className="drawing-info-verdict drawing-info-verdict--problem">
        <Icon name="error" />
        {t('drawingInfo.problems', { count: problems.length })}
      </span>
    );
  }
  return (
    <span className="drawing-info-verdict drawing-info-verdict--valid">
      <Icon name="check" />
      {t('drawingInfo.valid')}
      <span className="drawing-info-verdict-note">{t('drawingInfo.checkedAsYouDraw')}</span>
    </span>
  );
}

function ProblemEntry({
  problem,
  actions
}: {
  readonly problem: DrawingProblem;
  readonly actions: Omit<StructureChecksActions, 'onSelect'>;
}) {
  const { t } = useTranslation();
  const { issue } = problem;
  const message = issue ? structureIssueMessage(t, issue) : (problem.browserIssue?.message ?? '');
  const canConfirm = issue?.severity === 'WARNING' && issue.canConfirm && actions.onConfirm;
  return (
    <li className="drawing-info-problem">
      <span className={`drawing-info-severity drawing-info-severity--${problem.severity.toLowerCase()}`}>
        {t(`structureCheck.severity.${problem.severity}`)}
      </span>
      <div className="drawing-info-problem-body">
        <p>{message}</p>
        {(issue?.fixes?.length ?? 0) > 0 || canConfirm ? (
          <div className="drawing-info-fixes">
            {(issue?.fixes ?? []).map((fix, index) => (
              <button
                key={fix.kind}
                type="button"
                className={index === 0 ? 'drawing-info-fix drawing-info-fix--primary' : 'drawing-info-fix'}
                onPointerEnter={() => actions.onPreviewFix?.(fix)}
                onPointerLeave={() => actions.onCancelFix?.()}
                onFocus={() => actions.onPreviewFix?.(fix)}
                onBlur={() => actions.onCancelFix?.()}
                onClick={() => actions.onApplyFix?.(fix)}
              >
                {structureFixLabel(t, fix)}
              </button>
            ))}
            {canConfirm ? (
              <button type="button" className="drawing-info-fix" onClick={() => actions.onConfirm!(issue)}>
                {t('structureCheck.intended')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

function SmilesLine({
  smiles,
  formula,
  hapto = false
}: {
  readonly smiles: string | null;
  readonly formula: string;
  readonly hapto?: boolean;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  // #1130 (ADR 0024 D4): SMILES has no quintuple or sextuple bond -- say so, never show a wrong one.
  if (smiles === null) {
    return (
      <div className="drawing-info-smiles drawing-info-smiles--none">
        <span>{t('drawingInfo.noSmiles')}</span>
      </div>
    );
  }
  const label = copied ? t('drawingInfo.copied') : t('drawingInfo.copySmiles', { formula });
  return (
    <div className="drawing-info-smiles">
      <code>{smiles}</code>
      <button
        type="button"
        className="icon-button"
        aria-label={label}
        title={label}
        onClick={() =>
          void navigator.clipboard.writeText(smiles).then(
            () => setCopied(true),
            () => setCopied(false)
          )
        }
      >
        <Icon name={copied ? 'check' : 'copy'} />
      </button>
      {/* ADR 0026 Q1 (owner): SMILES can't write a hapto bond -- the pieces, and a note saying so. */}
      {hapto ? <p className="drawing-info-smiles-note">{t('drawingInfo.haptoSmiles')}</p> : null}
    </div>
  );
}
